import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { usePropertyTokens } from '../../Admin/Property/hooks/usePropertyTokens'
import { axiosInstance } from '../../../helpers'
import { PROPERTY_LEASING_SEASON_CONFIGS_KEY } from '../../LeasingSeasonConfig/useLeasingSeasonConfigApi'
import { DEFAULT_SEASON_CONFIG } from '../constants'
import {
    applyDemoProgressOverrides,
    buildDemoAiRecommendation,
    buildFallbackProgress,
    mapFunnelMetrics,
} from '../utils/demoProgress'
import {
    CreateLeasingSeasonRequest,
    LeasingSeasonConfig,
    LeasingSeasonDetail,
    LeasingSeasonListItem,
    LeverKey,
    SeasonProgressMetrics,
    TokenAllocations,
} from '../types/leasingSeason.types'
import {
    isViewingEffectiveCurrentSeason,
    resolveEffectiveCurrentSeasonId,
} from '../utils/seasonSelection'
import {
    SEASONS_LIST_KEY,
    seasonDetailKey,
    seasonsListKey,
} from './leasingSeasonKeys'
import { seasonEnrichmentAutomationsKey } from './useSeasonEnrichmentAutomations'
import {
    useLeasingSeasonDetail,
    useLeasingSeasonsList,
} from './useLeasingSeasonQueries'
import { getPriorSeason } from '../utils/priorSeason'
import { percentLeasedFromDetail } from '../utils/seasonProgressDisplay'

const seasonKey = seasonDetailKey

function listItemFromDetail(
    detail: LeasingSeasonDetail,
): LeasingSeasonListItem {
    return {
        id: detail.id,
        name: detail.name,
        start_date: detail.start_date,
        end_date: detail.end_date,
        goal_percent: detail.goal_percent,
        allocation_prospecting: detail.allocation_prospecting,
        allocation_pipeline: detail.allocation_pipeline,
        allocation_calls: detail.allocation_calls,
        allocation_website: detail.allocation_website,
        is_current: detail.is_current,
        is_archived: detail.is_archived,
        archived_at: detail.archived_at,
        created_date: detail.created_date,
        modified_date: detail.modified_date,
        application_signable_doc: detail.application_signable_doc,
        application_signable_doc_id: detail.application_signable_doc_id,
        require_application_review_before_screening:
            detail.require_application_review_before_screening,
        application_fee: detail.application_fee,
        guest_card_channels: detail.guest_card_channels,
        leasing_season_config_id: detail.leasing_season_config_id,
        leasing_season_config_name: detail.leasing_season_config_name,
        leasing_season_config_locked: detail.leasing_season_config_locked,
        pipeline_config_id: detail.pipeline_config_id,
        pipeline_config_name: detail.pipeline_config_name,
        pipeline_config_locked: detail.pipeline_config_locked,
        application_workbook_id: detail.application_workbook_id,
        application_workbook_name: detail.application_workbook_name,
        application_workbook_locked: detail.application_workbook_locked,
    }
}

function invalidateSeasonLists(queryClient: ReturnType<typeof useQueryClient>) {
    void queryClient.invalidateQueries({ queryKey: [SEASONS_LIST_KEY] })
}

function syncSeasonListCache(
    queryClient: ReturnType<typeof useQueryClient>,
    apartmentId: number | undefined,
    detail: LeasingSeasonDetail,
    options?: { setCurrentOnly?: boolean },
) {
    queryClient.setQueryData<LeasingSeasonListItem[]>(
        seasonsListKey(apartmentId),
        (old) => {
            if (!old) return old
            if (options?.setCurrentOnly) {
                return old.map((s) => ({
                    ...s,
                    is_current: s.id === detail.id,
                }))
            }
            return old.map((s) =>
                s.id === detail.id ? listItemFromDetail(detail) : s,
            )
        },
    )
}

function seasonToConfig(season: LeasingSeasonDetail): LeasingSeasonConfig {
    return {
        name: season.name,
        startDate: season.start_date,
        endDate: season.end_date,
        goalPercent: season.goal_percent,
        leaseTermId: season.lease_term_id ?? null,
        renewalDeadline: season.renewal_deadline ?? null,
        renewalGoalPercent: season.renewal_goal_percent ?? null,
        allocations: {
            prospecting: season.allocation_prospecting,
            pipeline: season.allocation_pipeline,
            calls: season.allocation_calls,
            website: season.allocation_website,
        },
        leasingSeasonConfigId: season.leasing_season_config_id ?? null,
        leasingSeasonConfigName: season.leasing_season_config_name ?? null,
        leasingSeasonConfigLocked: season.leasing_season_config_locked ?? false,
        pipelineConfigId: season.pipeline_config_id ?? null,
        pipelineConfigName: season.pipeline_config_name ?? null,
        pipelineConfigLocked: season.pipeline_config_locked ?? false,
        applicationWorkbookId: season.application_workbook_id ?? null,
        applicationWorkbookName: season.application_workbook_name ?? null,
        applicationWorkbookLocked: season.application_workbook_locked ?? false,
    }
}

function mapProgress(
    apiProgress: LeasingSeasonDetail['progress'],
): SeasonProgressMetrics {
    return {
        totalCapacity: apiProgress.total_capacity,
        signedNew: apiProgress.signed_new,
        signedRenewals: apiProgress.signed_renewals,
        leasedCount: apiProgress.leased_count,
        percentLeased: apiProgress.percent_leased,
        daysTotal: apiProgress.days_total,
        daysElapsed: apiProgress.days_elapsed,
        daysRemaining: apiProgress.days_remaining,
        expectedPercent: apiProgress.expected_percent,
        paceDelta: apiProgress.pace_delta,
        bedsToGoal: apiProgress.beds_to_goal,
        onPace: apiProgress.on_pace,
        behindBig: apiProgress.behind_big,
        isStub: apiProgress.is_stub,
    }
}

export function useLeasingSeason(apartmentId: number | undefined) {
    const queryClient = useQueryClient()
    const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(
        null,
    )
    const { tokenBalance } = usePropertyTokens(apartmentId?.toString() ?? '')

    const seasonsQuery = useLeasingSeasonsList(apartmentId)

    useEffect(() => {
        setSelectedSeasonId(null)
    }, [apartmentId])

    useEffect(() => {
        const list = seasonsQuery.data
        if (!list?.length) return

        if (selectedSeasonId && list.some((s) => s.id === selectedSeasonId)) {
            return
        }

        const current = list.find((s) => s.is_current) ?? list[0]
        setSelectedSeasonId(current.id)
    }, [seasonsQuery.data, selectedSeasonId])

    const seasonQuery = useLeasingSeasonDetail(selectedSeasonId)

    const createSeasonMutation = useMutation({
        mutationFn: (payload: CreateLeasingSeasonRequest) =>
            axiosInstance.post<LeasingSeasonDetail>(
                '/outreach/leasing-seasons/',
                payload,
            ),
        onError: () => toast.error('Failed to create leasing season'),
    })

    const updateSeasonMutation = useMutation({
        mutationFn: (patch: Record<string, unknown>) =>
            axiosInstance.patch<LeasingSeasonDetail>(
                `/outreach/leasing-seasons/${selectedSeasonId}/`,
                patch,
            ),
        onSuccess: ({ data }) => {
            toast.success('Season saved')
            queryClient.setQueryData(seasonKey(data.id), data)
            syncSeasonListCache(queryClient, apartmentId, data)
            queryClient.invalidateQueries({
                queryKey: seasonEnrichmentAutomationsKey(data.id),
            })
        },
        onError: () => toast.error('Failed to save season'),
    })

    const setCurrentMutation = useMutation({
        mutationFn: (seasonId: number) =>
            axiosInstance.post<LeasingSeasonDetail>(
                `/outreach/leasing-seasons/${seasonId}/set-current/`,
            ),
        onSuccess: ({ data }) => {
            toast.success(`${data.name} is now your current season`)
            queryClient.setQueryData(seasonKey(data.id), data)
            setSelectedSeasonId(data.id)
            invalidateSeasonLists(queryClient)
        },
        onError: () => toast.error('Failed to set current season'),
    })

    const archiveSeasonMutation = useMutation({
        mutationFn: (seasonId: number) =>
            axiosInstance.post<LeasingSeasonDetail>(
                `/outreach/leasing-seasons/${seasonId}/archive/`,
            ),
        onSuccess: ({ data }) => {
            toast.success(`Archived ${data.name}`)
            queryClient.setQueryData(seasonKey(data.id), data)
            if (selectedSeasonId === data.id) {
                setSelectedSeasonId(null)
            }
            invalidateSeasonLists(queryClient)
        },
        onError: (error: { response?: { data?: { detail?: string } } }) => {
            const detail = error?.response?.data?.detail
            toast.error(detail || 'Failed to archive season')
        },
    })

    const unarchiveSeasonMutation = useMutation({
        mutationFn: (seasonId: number) =>
            axiosInstance.post<LeasingSeasonDetail>(
                `/outreach/leasing-seasons/${seasonId}/unarchive/`,
            ),
        onSuccess: ({ data }) => {
            toast.success(`Restored ${data.name}`)
            queryClient.setQueryData(seasonKey(data.id), data)
            invalidateSeasonLists(queryClient)
        },
        onError: () => toast.error('Failed to restore season'),
    })

    const createSeasonWithConfig = useCallback(
        async (
            config: LeasingSeasonConfig,
            makeCurrent = true,
            rollover?: { sourceSeasonId: number; leadIds: number[] },
            onSuccess?: () => void,
        ) => {
            const { data } = await createSeasonMutation.mutateAsync({
                name: config.name,
                start_date: config.startDate,
                end_date: config.endDate,
                goal_percent: config.goalPercent,
                lease_term_id: config.leaseTermId ?? null,
                renewal_deadline: config.renewalDeadline || null,
                renewal_goal_percent: config.renewalGoalPercent ?? null,
                allocation_prospecting: config.allocations.prospecting,
                allocation_pipeline: config.allocations.pipeline,
                allocation_calls: config.allocations.calls,
                allocation_website: config.allocations.website,
                make_current: makeCurrent,
                leasing_season_config_id:
                    config.leasingSeasonConfigId ?? undefined,
            })

            let rolled = 0
            if (rollover?.leadIds.length) {
                try {
                    const rollResponse = await axiosInstance.post<{
                        rolled: number
                    }>(`/outreach/leasing-seasons/${data.id}/roll-leads/`, {
                        source_season_id: rollover.sourceSeasonId,
                        lead_ids: rollover.leadIds,
                    })
                    rolled = rollResponse.data.rolled
                } catch {
                    toast.error(
                        'Season created, but rolling leads forward failed.',
                    )
                }
            }

            toast.success(
                rolled > 0
                    ? `Leasing season created · ${rolled} lead${
                          rolled === 1 ? '' : 's'
                      } rolled forward`
                    : 'Leasing season created',
            )

            queryClient.setQueryData(seasonKey(data.id), data)
            setSelectedSeasonId(data.id)
            queryClient.invalidateQueries({ queryKey: [SEASONS_LIST_KEY] })
            queryClient.invalidateQueries({
                queryKey: [PROPERTY_LEASING_SEASON_CONFIGS_KEY],
            })
            onSuccess?.()
        },
        [createSeasonMutation, queryClient],
    )

    const updateSeason = useCallback(
        (patch: Partial<LeasingSeasonConfig>) => {
            if (!seasonQuery.data) return
            const payload: Record<string, unknown> = {}
            if (patch.name !== undefined) payload.name = patch.name
            if (patch.startDate !== undefined)
                payload.start_date = patch.startDate
            if (patch.endDate !== undefined) payload.end_date = patch.endDate
            if (patch.goalPercent !== undefined)
                payload.goal_percent = patch.goalPercent
            if (patch.leaseTermId !== undefined)
                payload.lease_term_id = patch.leaseTermId
            if (patch.renewalDeadline !== undefined)
                payload.renewal_deadline = patch.renewalDeadline || null
            if (patch.renewalGoalPercent !== undefined)
                payload.renewal_goal_percent = patch.renewalGoalPercent
            if (patch.allocations) {
                payload.allocation_prospecting = patch.allocations.prospecting
                payload.allocation_pipeline = patch.allocations.pipeline
                payload.allocation_calls = patch.allocations.calls
                payload.allocation_website = patch.allocations.website
            }
            updateSeasonMutation.mutate(payload)
        },
        [seasonQuery.data, updateSeasonMutation],
    )

    const saveSeasonConfig = useCallback(
        (config: LeasingSeasonConfig) => {
            updateSeason({
                name: config.name,
                startDate: config.startDate,
                endDate: config.endDate,
                goalPercent: config.goalPercent,
                leaseTermId: config.leaseTermId,
                renewalDeadline: config.renewalDeadline,
                renewalGoalPercent: config.renewalGoalPercent,
            })
        },
        [updateSeason],
    )

    const saveSeasonConfigForId = useCallback(
        (
            seasonId: number,
            config: LeasingSeasonConfig,
            onSuccess?: () => void,
        ) => {
            const payload: Record<string, unknown> = {
                name: config.name,
                start_date: config.startDate,
                end_date: config.endDate,
                goal_percent: config.goalPercent,
                lease_term_id: config.leaseTermId ?? null,
                renewal_deadline: config.renewalDeadline || null,
                renewal_goal_percent: config.renewalGoalPercent ?? null,
            }
            if (
                config.leasingSeasonConfigId != null &&
                !config.leasingSeasonConfigLocked
            ) {
                payload.leasing_season_config_id = config.leasingSeasonConfigId
            }
            axiosInstance
                .patch<LeasingSeasonDetail>(
                    `/outreach/leasing-seasons/${seasonId}/`,
                    payload,
                )
                .then(({ data }) => {
                    toast.success('Season saved')
                    queryClient.setQueryData(seasonKey(data.id), data)
                    syncSeasonListCache(queryClient, apartmentId, data)
                    onSuccess?.()
                })
                .catch(() => toast.error('Failed to save season'))
        },
        [apartmentId, queryClient],
    )

    const updateAllocation = useCallback(
        (key: LeverKey, value: number) => {
            if (!seasonQuery.data) return
            const current = seasonToConfig(seasonQuery.data)
            const others = ([
                'prospecting',
                'pipeline',
                'calls',
                'website',
            ] as LeverKey[]).filter((k) => k !== key)
            const remaining = 100 - value
            const currentOthersTotal =
                others.reduce((sum, k) => sum + current.allocations[k], 0) || 1
            const next: TokenAllocations = {
                ...current.allocations,
                [key]: value,
            }
            others.forEach((k) => {
                next[k] = Math.max(
                    0,
                    Math.round(
                        (current.allocations[k] / currentOthersTotal) *
                            remaining,
                    ),
                )
            })
            const drift = 100 - Object.values(next).reduce((a, b) => a + b, 0)
            if (others.length > 0) next[others[0]] += drift
            updateSeason({ allocations: next })
        },
        [seasonQuery.data, updateSeason],
    )

    const applyAiRecommendation = useCallback(
        (target: TokenAllocations) => {
            updateSeason({ allocations: target })
            toast.success('AI recommendation applied')
        },
        [updateSeason],
    )

    const resetAllocations = useCallback(() => {
        updateSeason({ allocations: DEFAULT_SEASON_CONFIG.allocations })
    }, [updateSeason])

    const seasonConfig = useMemo(
        () => (seasonQuery.data ? seasonToConfig(seasonQuery.data) : null),
        [seasonQuery.data],
    )

    const progress = useMemo(() => {
        if (!seasonConfig) return null
        if (seasonQuery.data?.progress) {
            return applyDemoProgressOverrides(
                mapProgress(seasonQuery.data.progress),
            )
        }
        return applyDemoProgressOverrides(buildFallbackProgress())
    }, [seasonQuery.data, seasonConfig])

    const aiRecommendation = useMemo(
        () => (seasonConfig ? buildDemoAiRecommendation() : null),
        [seasonConfig],
    )

    const funnelMetrics = useMemo(
        () => mapFunnelMetrics(seasonQuery.data?.progress),
        [seasonQuery.data],
    )

    const monthlyTokens = useMemo(() => {
        const perUnit = tokenBalance?.tokens_per_unit_allocated
        const units = tokenBalance?.billable_unit_count ?? 0
        if (perUnit && units > 0) return perUnit * units
        return null
    }, [tokenBalance])

    const isListLoading = Boolean(
        apartmentId && seasonsQuery.isLoading && !seasonsQuery.data,
    )
    const isDetailLoading = Boolean(
        selectedSeasonId && !seasonQuery.data && seasonQuery.isFetching,
    )

    const seasons = seasonsQuery.data ?? []

    const priorSeasonId = useMemo(() => {
        if (selectedSeasonId == null || !seasons.length) return null
        return getPriorSeason(seasons, selectedSeasonId)?.id ?? null
    }, [seasons, selectedSeasonId])

    const priorSeasonQuery = useLeasingSeasonDetail(priorSeasonId)

    const lastYearPercentLeased = useMemo(
        () => percentLeasedFromDetail(priorSeasonQuery.data ?? undefined),
        [priorSeasonQuery.data],
    )

    const effectiveCurrentSeasonId = useMemo(
        () => resolveEffectiveCurrentSeasonId(seasons),
        [seasons],
    )

    const isViewingCurrentSeason = useMemo(
        () => isViewingEffectiveCurrentSeason(seasons, selectedSeasonId),
        [seasons, selectedSeasonId],
    )

    const healCurrentAttemptedRef = useRef<number | null>(null)

    useEffect(() => {
        if (
            seasonsQuery.isLoading ||
            !seasonsQuery.isFetched ||
            !seasons.length ||
            seasons.some((s) => s.is_current)
        ) {
            return
        }

        const targetId = resolveEffectiveCurrentSeasonId(seasons)
        if (targetId == null || healCurrentAttemptedRef.current === targetId) {
            return
        }

        healCurrentAttemptedRef.current = targetId
        axiosInstance
            .post<LeasingSeasonDetail>(
                `/outreach/leasing-seasons/${targetId}/set-current/`,
            )
            .then(({ data }) => {
                queryClient.setQueryData(seasonKey(data.id), data)
                invalidateSeasonLists(queryClient)
            })
            .catch(() => {
                healCurrentAttemptedRef.current = null
            })
    }, [queryClient, seasons, seasonsQuery.isFetched, seasonsQuery.isLoading])

    return {
        seasons,
        season: seasonConfig,
        seasonDetail: seasonQuery.data ?? null,
        selectedSeasonId,
        effectiveCurrentSeasonId,
        isViewingCurrentSeason,
        setSelectedSeasonId,
        hasSeason: seasons.length > 0,
        isListLoading,
        isDetailLoading,
        isLoading: isListLoading || isDetailLoading,
        isSaving:
            updateSeasonMutation.isLoading || createSeasonMutation.isLoading,
        progress,
        aiRecommendation,
        funnelMetrics,
        lastYearPercentLeased,
        monthlyTokens,
        tokenBalance: tokenBalance?.current_balance ?? null,
        tokensSpentThisMonth: tokenBalance?.tokens_spent_this_month ?? 0,
        createSeasonWithConfig,
        saveSeasonConfig,
        saveSeasonConfigForId,
        updateSeason,
        updateAllocation,
        applyAiRecommendation,
        resetAllocations,
        setCurrentSeason: (id: number) => setCurrentMutation.mutate(id),
        isSettingCurrent: setCurrentMutation.isLoading,
        archiveSeason: (id: number) => archiveSeasonMutation.mutate(id),
        unarchiveSeason: (id: number) => unarchiveSeasonMutation.mutate(id),
        isArchivingSeason:
            archiveSeasonMutation.isLoading ||
            unarchiveSeasonMutation.isLoading,
    }
}

export type UseLeasingSeasonReturn = ReturnType<typeof useLeasingSeason>
